import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TOrganisation, TOrgUser } from '@answerr/types';
import { ToastInfo } from '../types/common.types';

interface CommonState {
  orgInfo: TOrganisation | null;
  accessToken: string;
  sessionId: string;
  toastInfo: ToastInfo | null;
  userInfo: TOrgUser | null;
  isSuperAdmin: boolean;
  // Campaign whose assessment generation the floating notification tracks.
  acraGeneratingCampaignId: string | null;
}

const getPersistedValue = <T>(key: string): T | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const persistedOrgInfo = getPersistedValue<TOrganisation>('orgInfo');
const persistedUserInfo = getPersistedValue<TOrgUser>('userInfo');
const persistedSessionId =
  typeof window === 'undefined' ? '' : localStorage.getItem('sessionId') || '';
const persistedAccessToken =
  typeof window === 'undefined' ? '' : localStorage.getItem('accessToken') || '';

const persistedAcraGeneratingCampaignId =
  typeof window === 'undefined'
    ? null
    : localStorage.getItem('acra:generatingCampaignId');

const initialState: CommonState = {
  orgInfo: persistedOrgInfo,
  accessToken: persistedAccessToken,
  sessionId: persistedSessionId,
  toastInfo: null,
  userInfo: persistedUserInfo,
  isSuperAdmin: Boolean(persistedOrgInfo || persistedUserInfo),
  acraGeneratingCampaignId: persistedAcraGeneratingCampaignId,
};

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setOrgInfo: (state, action: PayloadAction<TOrganisation | null>) => {
      state.orgInfo = action.payload;
    },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setToastInfo: (state, action: PayloadAction<ToastInfo | null>) => {
      state.toastInfo = action.payload;
    },
    setUserInfo: (state, action: PayloadAction<TOrgUser | null>) => {
      state.userInfo = action.payload;
    },
    setIsSuperAdmin: (state, action: PayloadAction<boolean>) => {
      state.isSuperAdmin = action.payload;
    },
    // Mirrored to localStorage so a page refresh resumes tracking
    // (same trick as aiq:lastJobId in the consumer app).
    setAcraGeneratingCampaignId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.acraGeneratingCampaignId = action.payload;
      if (typeof window !== 'undefined') {
        if (action.payload) {
          localStorage.setItem('acra:generatingCampaignId', action.payload);
        } else {
          localStorage.removeItem('acra:generatingCampaignId');
        }
      }
    },
  },
});

export const { setOrgInfo, setAccessToken, setSessionId, setToastInfo, setUserInfo, setIsSuperAdmin, setAcraGeneratingCampaignId } = commonSlice.actions;

export default commonSlice.reducer;